import { Button, Checkbox, Divider, FormControlLabel, Grid2 as Grid, Radio, Stack, useMediaQuery, useTheme } from "@mui/material";
import copy from "fast-copy";
import { matchIsValidColor, MuiColorInput } from "mui-color-input";
import { useSnackbar } from "notistack";
import { paperSizes } from "pdf-generator";
import React from "react";
import { HighlightSelectBox, renderingOptions, TextField } from "../../../components";
import { SelectBoxOption } from "../../../interfaces";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { useGetDocumentTemplatesDDOptionsQuery, useUpdateQuoteMutation } from "../../../redux/services";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { closeDialog } from "../../../redux/slices/dialog";
import { updateQuoteSlice } from "../../../redux/slices/quote";
import { orientationOptions, paperSizeOptions } from "../../documentTemplates/dashboard/addEditTemplate";
import { preparePayload } from "../utils";

interface iState {
    name: string,
    paperSize: SelectBoxOption | null,
    orientation: SelectBoxOption | null,
    template: SelectBoxOption | null,
    tableHeaderColor: string,
    useMBFormatter: boolean,
    includeSearialNo: boolean
}

export const QuoteReportConfig: React.FC<{ quoteId: string, onSaved: () => void }> = ({ quoteId, onSaved }) => {

    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { quote } = useAppSelector(store => store);
    const matches = useMediaQuery(theme.breakpoints.down('md'));

    const templateOptions = useGetDocumentTemplatesDDOptionsQuery({}, { refetchOnFocus: true, refetchOnMountOrArgChange: true, });
    const [updateQuote, { isLoading }] = useUpdateQuoteMutation();

    const [state, setState] = React.useState<iState>({
        name: quote.quote_report?.name ?? quote.name,
        paperSize: quote.quote_report?.paper_size ? paperSizeOptions.find(option => option.value === quote.quote_report?.paper_size) as SelectBoxOption : paperSizes[4],
        orientation: quote.quote_report?.orientation ? orientationOptions.find(option => option.value === quote.quote_report?.orientation) as SelectBoxOption : orientationOptions[0],
        tableHeaderColor: quote.quote_report?.table_header_color ?? "#d1c4e9",
        useMBFormatter: quote.quote_report?.use_mb_formatter ?? false,
        template: quote.quote_report?.template_id ? templateOptions.data?.find(option => option.value === quote.quote_report?.template_id) as SelectBoxOption : null,
        includeSearialNo: quote.quote_report?.include_serial_no ?? false,
    });

    const [renderingType, setRenderingType] = React.useState(quote.hfTemplate ? renderingOptions[1].value : renderingOptions[0].value);

    const options = templateOptions?.data ?? [];

    const saveItemDetails = async () => {

        let newQuote = copy(quote);
        
        newQuote.quote_report = {
            ...newQuote.quote_report,
            name: state.name,
            paper_size: state.paperSize?.value as string,
            orientation: state.orientation?.value as string,
            template_id: state.template?.value as string,
            table_header_color: state.tableHeaderColor,
            use_mb_formatter: state.useMBFormatter,
        }

        newQuote.report_config.include_serial_num = state.includeSearialNo;

        let payload = { ...preparePayload({ ...quote, ...newQuote }) };

        if (!quote.released) {
            dispatch(openBackdrop('Saving Quote Report Configuration...'));
            await updateQuote({ id: quoteId, payload });
            enqueueSnackbar("Quote report configuration saved successfully!", { variant: "success" });
            onSaved();
            dispatch(closeDialog());
            dispatch(closeBackdrop());
        };

        dispatch(updateQuoteSlice({ ...quote, ...newQuote }));
    };

    React.useEffect(() => {
        setState({ ...state, template: quote.quote_report?.template_id ? templateOptions.data?.find(option => option.value === quote.quote_report?.template_id) as SelectBoxOption : null })
        // eslint-disable-next-line
    }, [options]);

    return <Stack gap={2} sx={{ position: "relative" }}>
        <TextField
            variant="filled"
            label={"Name"}
            size={"small"}
            fullWidth
            required
            value={state.name}
            onChange={(e) => setState({ ...state, name: e.target.value })}
        />

        <Divider variant="fullWidth" />

        <Grid container spacing={1} width={"100%"} columns={13}>
            <Grid size={{ xs: 13, md: 6 }}>
                <Stack spacing={2}>
                    <FormControlLabel
                        value={renderingOptions[0].value}
                        control={
                            <Radio
                                size="small"
                                checked={renderingOptions[0]?.value === renderingType}
                                onChange={() => {
                                    setRenderingType(renderingOptions[0].value);
                                    setState({ ...state, template: null, paperSize: paperSizes[4], orientation: orientationOptions[0] })
                                }}
                                value={renderingOptions[0].value} />
                        }
                        label={renderingOptions[0].label}
                    />
                    <HighlightSelectBox
                        disabled={renderingOptions[1]?.value === renderingType}
                        label={"Paper Size"}
                        margin={"none"}
                        fullWdith
                        value={state.paperSize}
                        options={paperSizeOptions}
                        onChange={(paperSize: SelectBoxOption) => { setState({ ...state, paperSize }) }}
                    />

                    <HighlightSelectBox
                        disabled={renderingOptions[1]?.value === renderingType}
                        label={"Orientation"}
                        margin={"none"}
                        fullWdith
                        value={state.orientation}
                        options={orientationOptions}
                        onChange={(orientation: SelectBoxOption) => { setState({ ...state, orientation }) }}
                    />
                </Stack>
            </Grid>

            <Grid
                mt={2}
                size={{
                    xs: 13,
                    md: 1
                }}>
                <Stack sx={{ width: "100%", height: "100%" }} justifyContent={matches ? "center" : "flex-start"} alignItems={matches ? "stretch" : 'center'}>
                    <Divider orientation={matches ? "horizontal" : "vertical"} />
                </Stack>
            </Grid>

            <Grid
                size={{
                    xs: 13,
                    md: 6
                }}>
                <Stack spacing={2}>
                    <FormControlLabel
                        value={renderingOptions[1].value}
                        control={
                            <Radio
                                size="small"
                                checked={renderingOptions[1].value === renderingType}
                                onChange={() => {
                                    setRenderingType(renderingOptions[1].value);
                                    setState({ ...state, paperSize: null, orientation: null });
                                }}
                                value={renderingOptions[1].value}
                                name="radio-buttons"
                            />
                        }
                        label={renderingOptions[1].label}
                    />

                    <HighlightSelectBox
                        disabled={renderingOptions[0]?.value === renderingType}
                        label={"Select template"}
                        margin={"none"}
                        fullWdith
                        loading={templateOptions.isLoading || templateOptions.isFetching}
                        value={state.template}
                        options={options}
                        onChange={(template: SelectBoxOption) => { setState({ ...state, template }) }}
                        helperText={`Caution! Please ensure the table width is not exceeding the selected template page width! Please select appropriate template to accommodate table content.`}
                    />
                </Stack>
            </Grid>

            <Grid size={{ xs: 13 }} sx={{ mt: 2, mb: 2 }}>
                <Divider variant="fullWidth" />
            </Grid>

            <Grid size={{ xs: 13 }}>
                <MuiColorInput
                    fullWidth
                    label={"Table Header Color"}
                    size={"small"}
                    fallbackValue={"#d1c4e9"}
                    value={state.tableHeaderColor}
                    onChange={(tableHeaderColor: string) => {
                        if (matchIsValidColor(tableHeaderColor)) {
                            setState({ ...state, tableHeaderColor })
                        }
                    }}
                />
            </Grid>

            <Grid size={{ xs: 13, sm: 7 }} sx={{ mt: 1 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={state.useMBFormatter}
                            onChange={() => {
                                setState({ ...state, useMBFormatter: !state.useMBFormatter })
                            }}
                        />
                    }
                    label={"Include Millions & Billions Formatting"}
                />
            </Grid>

            <Grid size={{ xs: 13, sm: 6 }} sx={{ mt: 1 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={state.includeSearialNo}
                            onChange={() => {
                                setState({ ...state, includeSearialNo: !state.includeSearialNo })
                            }}
                        />
                    }
                    label={"Include Serial Number"}
                />
            </Grid>

            <Grid size={{ xs: 12, sm: 7 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            disabled
                            checked={true}
                        />
                    }
                    label={"Show Footer URL"}
                />
            </Grid>
        </Grid>

        <Stack direction={{ xs: "column", sm: "row" }} gap={1} sx={{ justifyContent: "end", position: "absolute", bottom: 0, right: 0 }}>
            <Button variant="outlined" onClick={() => dispatch(closeDialog())}>Cancel</Button>
            <Button variant="contained" disabled={isLoading} onClick={() => saveItemDetails()}>{`Generate Report`}</Button>
        </Stack>
    </Stack>
}